/**
 * Phase 11 — bootstraps the platform-ops handler registry at app
 * boot. Called once by `PlatformOpsProvider` on first mount.
 *
 * Each `ide_client` op gets one `register(...)` call. Adding a new op
 * to the catalog (X.2 onward) typically means: add a handler module
 * here, import it, register it.
 */
import { platformOpsRegistry } from "@/lib/platform-ops-registry";
import { ideToastShowHandler } from "./ide-toast-show";
import {
  ideChannelsConnectHandler,
  ideContactsDetailHandler,
  ideContactsOpenHandler,
  ideIdentityOpenHandler,
  ideOnboardingOpenHandler,
} from "./ide-contacts-nav";
import { ideScheduleOpenHandler } from "./ide-schedule-nav";

let bootstrapped = false;

/**
 * Register every shipped IDE-client handler. Idempotent — safe to call
 * from multiple mount points; only registers once per process.
 */
export function registerPlatformOpsHandlers(): void {
  if (bootstrapped) return;
  bootstrapped = true;

  platformOpsRegistry.register("ide.toast.show", ideToastShowHandler);

  // Contacts program §2.5 — navigation-only ops (web channel only; the
  // platform-ops IM gate keeps them off IM turns runtime-side).
  platformOpsRegistry.register("ide.contacts.open", ideContactsOpenHandler);
  platformOpsRegistry.register("ide.contacts.detail", ideContactsDetailHandler);
  platformOpsRegistry.register("ide.identity.open", ideIdentityOpenHandler);
  platformOpsRegistry.register("ide.channels.connect", ideChannelsConnectHandler);
  platformOpsRegistry.register("ide.onboarding.open", ideOnboardingOpenHandler);

  // Agendamentos (R2 D4) — contract frozen by the recurring-jobs lane's P0
  // ruling 5. Registered ahead of their runtime roll: inert until they emit
  // it, live the moment they do, with no second SPA deploy.
  platformOpsRegistry.register("ide.schedule.open", ideScheduleOpenHandler);

  // X.2 will add: ide.case.open, ide.case.create, ide.matter.create,
  // ide.navigate, ide.confirm.request.
}

/** Test-only — resets the bootstrap flag + registry so vitest specs can
 *  re-register from a clean slate. */
export function _resetPlatformOpsHandlersForTest(): void {
  bootstrapped = false;
  platformOpsRegistry._reset();
}
